import { Node } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"

import { normalizeFirstPartyImageSrc } from "@/tiptap/core/editor-clipboard"
import {
  isEmojiFallbackImage,
  parseImageAttributes,
} from "@/tiptap/nodes/image-node/image-node-extension"

const BLOCK_SIBLING_TAGS = new Set([
  "P", "H1", "H2", "H3", "H4", "H5", "H6",
  "DIV", "FIGURE", "DETAILS", "UL", "OL", "TABLE", "BLOCKQUOTE",
  "PRE", "HR", "SECTION", "ARTICLE",
])

function hasNonWhitespaceInlineSibling(img) {
  const parent = img.parentNode
  if (!parent) return false

  return Array.from(parent.childNodes).some((node) => {
    if (node === img) return false
    if (node.nodeType === 3) return Boolean(node.textContent?.trim())
    if (!(node instanceof Element)) return false
    if (BLOCK_SIBLING_TAGS.has(node.tagName)) return false
    return Boolean(node.textContent?.trim())
  })
}

const INLINE_IMAGE_RESIZE_KEY = new PluginKey("inlineImageResize")
const RESIZE_EDGE_PX = 8
const MIN_INLINE_IMAGE_SIZE = 16

export function resizeEdgeAt(rect, clientX, clientY, threshold = RESIZE_EDGE_PX) {
  const left = clientX - rect.left <= threshold
  const right = rect.right - clientX <= threshold
  const top = clientY - rect.top <= threshold
  const bottom = rect.bottom - clientY <= threshold
  if (!left && !right && !top && !bottom) return null

  return { left, right, top, bottom }
}

export function cursorForResizeEdge(edge) {
  if (!edge) return null

  const { left, right, top, bottom } = edge
  if ((left || right) && (top || bottom)) {
    return (left && top) || (right && bottom) ? "nwse-resize" : "nesw-resize"
  }
  if (left || right) return "ew-resize"
  return "ns-resize"
}

export function sizeFromResize({ edge, start, current, minSize = MIN_INLINE_IMAGE_SIZE }) {
  let width = start.width
  let height = start.height
  const dx = current.x - start.x
  const dy = current.y - start.y

  if (edge.right) width = start.width + dx
  if (edge.left) width = start.width - dx
  if (edge.bottom) height = start.height + dy
  if (edge.top) height = start.height - dy

  if (start.width > 0 && start.height > 0) {
    if ((edge.left || edge.right) && !(edge.top || edge.bottom)) {
      height = start.height * (width / start.width)
    } else if ((edge.top || edge.bottom) && !(edge.left || edge.right)) {
      width = start.width * (height / start.height)
    } else {
      height = start.height * (width / start.width)
    }

    const scale = Math.max(
      width / start.width,
      minSize / start.width,
      minSize / start.height,
    )
    return {
      width: Math.round(start.width * scale),
      height: Math.round(start.height * scale),
    }
  }

  return {
    width: Math.max(minSize, Math.round(width)),
    height: Math.max(minSize, Math.round(height)),
  }
}

const RESIZE_CURSOR_CLASSES = [
  "is-resize-ew",
  "is-resize-ns",
  "is-resize-nwse",
  "is-resize-nesw",
]

const RESIZE_CURSOR_CLASS_BY_CURSOR = {
  "ew-resize": "is-resize-ew",
  "ns-resize": "is-resize-ns",
  "nwse-resize": "is-resize-nwse",
  "nesw-resize": "is-resize-nesw",
}

function applyInlineImageSize(img, width, height) {
  img.setAttribute("width", String(width))
  img.setAttribute("height", String(height))
  img.style.width = `${width}px`
  img.style.height = `${height}px`
}

function setResizeCursorClass(img, cursor) {
  img.classList.remove(...RESIZE_CURSOR_CLASSES)
  const className = RESIZE_CURSOR_CLASS_BY_CURSOR[cursor]
  if (className) img.classList.add(className)
}

function attrsForInlineImage(img) {
  if (!(img instanceof Element)) return false
  if (isEmojiFallbackImage(img)) return false
  if (img.closest("figure")) return false

  return parseImageAttributes(img)
}

export const InlineImage = Node.create({
  name: "inlineImage",
  inline: true,
  group: "inline",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => normalizeFirstPartyImageSrc(element.getAttribute("src")),
      },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      height: { default: null },
      style: { default: null },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'img[data-inline="true"]',
        priority: 60,
        getAttrs: attrsForInlineImage,
      },
      {
        tag: "img[src]",
        priority: 60,
        getAttrs: (img) => {
          const attrs = attrsForInlineImage(img)
          if (!attrs) return false
          if (img.getAttribute("data-inline") === "true") return attrs
          if (!hasNonWhitespaceInlineSibling(img)) return false
          return attrs
        },
      },
    ]
  },

  renderHTML({ node }) {
    const imgAttrs = {
      src: node.attrs.src || "",
      "data-inline": "true",
    }

    if (node.attrs.alt) imgAttrs.alt = node.attrs.alt
    if (node.attrs.title) imgAttrs.title = node.attrs.title
    if (node.attrs.width) imgAttrs.width = node.attrs.width
    if (node.attrs.height) imgAttrs.height = node.attrs.height
    if (node.attrs.style) imgAttrs.style = node.attrs.style

    return ["img", imgAttrs]
  },

  addProseMirrorPlugins() {
    const imageType = this.type
    let drag = null

    const commitResize = (view) => {
      if (!drag) return

      const { pos, width, height, img } = drag
      drag = null
      setResizeCursorClass(img, null)

      const node = view.state.doc.nodeAt(pos)
      if (!node || node.type !== imageType) return

      view.dispatch(
        view.state.tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          width,
          height,
        }),
      )
    }

    return [
      new Plugin({
        key: INLINE_IMAGE_RESIZE_KEY,
        view(editorView) {
          const onMove = (event) => {
            if (!drag) return

            const size = sizeFromResize({
              edge: drag.edge,
              start: drag.start,
              current: { x: event.clientX, y: event.clientY },
            })
            drag.width = size.width
            drag.height = size.height
            applyInlineImageSize(drag.img, size.width, size.height)
          }

          const onUp = () => commitResize(editorView)

          window.addEventListener("mousemove", onMove)
          window.addEventListener("mouseup", onUp)
          return {
            destroy() {
              window.removeEventListener("mousemove", onMove)
              window.removeEventListener("mouseup", onUp)
            },
          }
        },
        props: {
          handleDOMEvents: {
            mousedown(view, event) {
              if (!view.editable || event.button !== 0) return false

              const img = event.target
              if (!(img instanceof HTMLImageElement)) return false
              if (img.getAttribute("data-inline") !== "true") return false
              if (!img.classList.contains("ProseMirror-selectednode")) return false

              const rect = img.getBoundingClientRect()
              const edge = resizeEdgeAt(rect, event.clientX, event.clientY)
              if (!edge) return false

              let pos
              try {
                pos = view.posAtDOM(img, 0)
              } catch {
                return false
              }

              event.preventDefault()
              drag = {
                pos,
                img,
                edge,
                start: {
                  x: event.clientX,
                  y: event.clientY,
                  width: rect.width,
                  height: rect.height,
                },
                width: Math.round(rect.width),
                height: Math.round(rect.height),
              }
              return true
            },
            mousemove(_view, event) {
              if (drag) return false

              const img = event.target
              if (!(img instanceof HTMLImageElement)) return false
              if (img.getAttribute("data-inline") !== "true") return false
              if (!img.classList.contains("ProseMirror-selectednode")) {
                setResizeCursorClass(img, null)
                return false
              }

              const edge = resizeEdgeAt(img.getBoundingClientRect(), event.clientX, event.clientY)
              setResizeCursorClass(img, cursorForResizeEdge(edge))
              return false
            },
            mouseleave(_view, event) {
              const img = event.target
              if (!(img instanceof HTMLImageElement)) return false
              if (!drag) setResizeCursorClass(img, null)
              return false
            },
          },
        },
      }),
    ]
  },
})

export default InlineImage
